// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/holdRailProgress.test.ts
import { useMemo, useRef } from "react";

import { type AskV2RailContent } from "./types";

// why: one shared object, so a held idle section keeps the same identity across renders.
const EMPTY_PROGRESS: NonNullable<AskV2RailContent["progress"]> = { items: [] };

/**
 * Keep the Progress section once it has appeared.
 *
 * A turn with no review (the setup chat, a plain question) still publishes a
 * plan while it runs, so Progress arrived with every message and vanished when
 * the turn ended. When it was the rail's only section the whole rail mounted
 * and unmounted with it, and the thread column shrank and grew on every send.
 * Once held, a turn with nothing running keeps an empty Progress section,
 * which the rail draws as "Nothing is actively in progress".
 */
export function holdRailProgress(
  content: AskV2RailContent,
  held: boolean,
): AskV2RailContent {
  if (!held || content.progress != null) return content;
  return { ...content, progress: EMPTY_PROGRESS };
}

/**
 * Remembers, per `scopeKey` (the chat or the project), whether Progress has
 * shown yet, and holds it from then on. A new scope starts unheld.
 */
export function useHeldRailProgress(
  content: AskV2RailContent,
  scopeKey: string,
): AskV2RailContent {
  const heldRef = useRef({ scopeKey, held: false });
  if (heldRef.current.scopeKey !== scopeKey) {
    heldRef.current = { scopeKey, held: false };
  }
  // why: a ref, not state; flipping it during render is idempotent, and state would cost a second render with the rail already unmounted.
  if (content.progress != null) heldRef.current.held = true;
  const held = heldRef.current.held;
  // why: memoized so `railContent` keeps its identity between renders, like the bare `useMemo` it wraps; the context memo keyed on it would otherwise rebuild every render.
  return useMemo(() => holdRailProgress(content, held), [content, held]);
}
